/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { getRenderOption } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'bar',
  name_en: 'Bar',
  // name_zh: '柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    z: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: [DEFINE.ARRAY, DEFINE.SINGLE]
    },
    z: {
      itemType: [DEFINE.DATE, DEFINE.STR],
      category: DEFINE.SINGLE,
      priority: DEFINE.STR,
      optional: true
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string[];
    z?: string;
  };
  showBackground?: boolean;
  /** 是否堆叠 */
  dvStack?: boolean;
  dvHorizontal?: boolean;
}
/**
 * 柱状图融合了堆叠、水平条形图、分组、多Y轴的情况，有着较为复杂的处理逻辑
 */
export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: false,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util: utils, hook, layout, token } = api;
    const {
      dataIndex = 0,
      encoding: { x, y: yTemp, z },
      showBackground = false,
      dvStack = false,
      dvHorizontal = false
    } = spec;
    const { proxy } = api.spec;
    let y = Array.isArray(yTemp) ? yTemp : [yTemp];
    /** 多y轴，多维指标z存在 */
    const isMultiYAxisWithZ = y.length >= 2 && typeof z !== 'undefined';
    // 柱状图不支持多Y轴与第三维度指标（z）同时存在，若是同时存在，则将多y轴降为单Y轴（多Y轴包含了第三维度指标）
    if (isMultiYAxisWithZ) {
      y = [y[0]];
    }
    const yLen = y.length;
    /** 多y轴 */
    const isMultiYAxis = yLen >= 2;
    /** 单y轴，多维指标z存在 */
    const isSingleYAxisWithZ = yLen < 2 && typeof z !== 'undefined';
    /** 单y轴，多维指标z不存在 */
    const singleYAxisWithoutZ = yLen < 2 && typeof z === 'undefined';

    // * ------------------------------------ data
    const data = dataArray[dataIndex];
    const transformData = utils.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' }
    ]);
    const {
      x: xAxisData,
      y: yAxisData,
      z: seriesAttributeArray = y
    } = utils.dataEncoding.encoding(transformData, {
      x: {
        field: x,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      y: { field: y[0], transforms: [{ type: 'toNumeric' }] },
      z: { field: z!, transforms: [{ type: 'deDuplication' }] }
    });

    // * ------------------------------------ chart
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption,
      right: dvHorizontal ? 20 : baseGridOption.right,
      top: dvHorizontal && isMultiYAxis ? 0 : baseGridOption.top
    };

    // 计算单个柱子的宽度
    const barWidth =
      ((chartViewRect.width - (dvHorizontal ? grid.top + grid.bottom : grid.left + grid.right)) /
        (xAxisData.length * seriesAttributeArray.length)) *
      0.8;
    // 允许的柱子的最大宽度
    const barMaxWidth =
      (presetOption.series as []).find(
        seriesItem => (seriesItem as { type: string }).type === 'bar'
      )?.barMaxWidth ?? utils.const.BAR_MAX_WIDTH;

    // 处理dataZoom相关信息
    const dataZoomInfo = hook.layout(api).isShowDataZoom(layout, xAxisData.length);
    const showDataZoom = dvHorizontal ? barWidth <= 12 / 0.8 : dataZoomInfo.showDataZoom;
    const { dataZoomLimit } = dataZoomInfo;
    const [dataZoomStartIndex, dataZoomEndIndex] = [
      xAxisData.length - dataZoomLimit,
      xAxisData.length - 1
    ];

    // 根据y轴和多维指标z来判断series的遍历数组
    let seriesMapArray: string[] = [];
    if (singleYAxisWithoutZ) {
      seriesMapArray = y;
    }
    if (isSingleYAxisWithZ) {
      seriesMapArray = seriesAttributeArray;
    }
    if (isMultiYAxis) {
      seriesMapArray = y;
    }

    // 获取series的数据
    const series = seriesMapArray.map((name, index) => {
      let seriesData;
      // 单Y轴、三维度数据处理
      if (typeof z !== 'undefined') {
        const dataset = utils.dataTransform.transform(transformData, [
          { type: 'filter', cb: dataItem => dataItem[z] === name }
        ]);
        seriesData = xAxisData.map(date => {
          const dataItem = utils.dataTransform.transform(dataset.slice(), [
            { type: 'find', cb: item => item[x] === date }
          ]);
          return dataItem ? +dataItem?.[y[0]] : null;
        });
        // 多Y轴数据处理
      } else if (isMultiYAxis) {
        const yData = utils.dataEncoding.encoding(transformData, {
          y: { field: y[index], transforms: [{ type: 'toNumeric' }] }
        });
        seriesData = yData.y;
        // 单Y轴数据处理
      } else {
        seriesData = yAxisData;
      }

      return {
        name,
        type: 'bar',
        data: seriesData,
        stack: dvStack ? 'Total' : undefined,
        showBackground,
        // 根据条形图对应的x、y坐标轴不同而判断
        yAxisIndex: 0,
        xAxisIndex: dvHorizontal && typeof z === 'undefined' ? index : 0,
        __encodeField: name,
        label: {
          formatter(params: any) {
            const result = hook.formatDataNumberValue(params.value, {
              type: 'table',
              unit: dataMetaInfo[name]?.unit
            });
            return result.value + result.unit;
          }
        }
      };
    });

    // 水平dataZoom
    const verticalDataZoom = {
      type: 'slider',
      show: showDataZoom,
      height: 16,
      bottom: 8,
      startValue: dataZoomStartIndex,
      endValue: dataZoomEndIndex,
      moveHandleIcon: 'path://',
      moveHandleSize: -32,
      moveHandleStyle: {
        opacity: 0
      },
      dvMoveHandleOffset: -16,
      borderRadius: 1,
      handleSize: '100%',
      dataBackground: {
        areaStyle: {
          opacity: 0
        },
        lineStyle: {
          opacity: 0
        }
      },
      selectedDataBackground: {
        areaStyle: {
          opacity: 0
        },
        lineStyle: {
          opacity: 0
        }
      }
    };
    // 竖直方向dataZoom显示元素个数
    const showBarNum = 8;
    // 竖直方向dataZoom
    const horizontalDataZoom = {
      type: 'slider',
      show: showDataZoom,
      orient: 'vertical',
      width: 8,
      startValue: dataZoomEndIndex - (showBarNum - 1),
      endValue: dataZoomEndIndex,
      handleSize: 0,
      fillerColor: token?.dvStandardChart?.baseOption?.dataZoom[0]?.dvScrollColor || '#DADDE0',
      borderColor: 'transparent',
      borderRadius: 0,
      showDetail: false,
      showDataShadow: false,
      zoomLock: true,
      brushSelect: false
    };

    // y轴配置
    const yAxis = [y[0]].map((_, index) => {
      return {
        __dataIndex: index,
        __encodeField: y,
        show: true,
        type: 'value',
        name: y[index] + (dataMetaInfo[y[index]]?.unit ? `(${dataMetaInfo[y[index]].unit})` : ''),
        dvSplitLineNumber: hook.getSplitLineNumber(
          chartViewRect.height - (dvHorizontal ? grid.left + grid.right : grid.top + grid.bottom)
        ),
        axisLine: {
          show: dvHorizontal,
          onZero: false
        },
        z: undefined
      };
    });

    const option = {
      grid: {
        ...grid,
        bottom: showDataZoom && !dvHorizontal ? 24 + grid.bottom : grid.bottom,
        right: showDataZoom ? grid.right : 0
      },
      tooltip: {
        trigger: 'axis'
      },
      xAxis: [
        {
          __dataIndex: dataIndex,
          __encodeField: x,
          show: true,
          type: 'category',
          axisLabel: {
            dvAlignEdge: !dvHorizontal
          },
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: xAxisData,
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              dvMaxWidth:
                barWidth > barMaxWidth
                  ? barMaxWidth * utils.const.SHADOW_WIDTH_RATIO * seriesAttributeArray.length
                  : undefined
            },
            z: 0
          }
        }
      ],
      yAxis,
      dataZoom: showDataZoom
        ? [dvHorizontal ? horizontalDataZoom : verticalDataZoom]
        : [
            {
              orient: dvHorizontal ? 'vertical' : 'horizontal'
            }
          ],
      series
    };

    // 条形图时，对调x、y轴的配置
    if (dvHorizontal) {
      const temp = option.xAxis;
      option.xAxis = option.yAxis;
      option.yAxis = temp;
    }

    return proxy ? getRenderOption(option) : option;
  }
});
